import { Autocomplete, CircularProgress, TextField, InputAdornment, Typography } from '@mui/material';
import type { SlackChannelOption } from '@vy/types';
import { isNotNullOrUndefined, isString } from '@vy/utils';
import type { Control, FieldValues, Path, PathValue } from 'react-hook-form';
import { Controller } from 'react-hook-form';

type ControlledSlackChannelSelectProps<FormValues extends FieldValues> = {
  name: Path<FormValues>;
  control: Control<FormValues>;
  label?: string;
  placeholder?: string;
  defaultValue?: PathValue<FormValues, Path<FormValues>>;
  disabled?: boolean;
  channels: SlackChannelOption[];
  isLoading?: boolean;
};

export const ControlledSlackChannelSelect = <FormValues extends FieldValues>({
  control,
  label,
  placeholder,
  name,
  defaultValue,
  disabled,
  channels,
  isLoading = false,
}: ControlledSlackChannelSelectProps<FormValues>) => {
  const getChannelById = (channelId: string): SlackChannelOption | null => {
    return channels.find((channel) => channel.id === channelId) || null;
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          fullWidth
          disabled={disabled || isLoading}
          autoHighlight
          options={channels}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          value={isString(field.value) ? getChannelById(field.value) : field.value}
          onChange={(_, newValue) => {
            field.onChange(isNotNullOrUndefined(newValue?.id) ? newValue.id : undefined);
          }}
          loading={isLoading}
          filterOptions={(options, { inputValue }) => {
            const filterValue = inputValue.toLowerCase();
            return options.filter((option) =>
              option.name.toLowerCase().includes(filterValue)
            );
          }}
          renderOption={(props, option) => (
            <li {...props}>
              <Typography>#{option.name}</Typography>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              helperText={isNotNullOrUndefined(error) ? error.message : ''}
              error={isNotNullOrUndefined(error)}
              slotProps={{
                ...params.slotProps,

                input: {
                  ...params.slotProps.input,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body1" sx={{
                        color: "text.primary"
                      }}>
                        #
                      </Typography>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },

                htmlInput: {
                  ...params.slotProps.htmlInput,
                  autoComplete: 'new-password',
                }
              }} />
          )}
          noOptionsText={isLoading ? 'Loading channels...' : 'No channels found'}
        />
      )}
    />
  );
}; 